import { Asset, AssetMatchResponse, FolderContent } from '@/api/generatedApi';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowParams,
  GridApiPro,
  GridPreferencePanelsValue,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, UnfoldMore, UnfoldLess, ViewColumn } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { DataGridLazy } from '../DataGrid/DataGridLazy';
import { DraggableRow } from '../DataGrid/DraggableRow';
import { ApiErrorDisplay } from '@/types/apiError';
import { RenderPercentage } from '../Search/SearchTable/cellDefinitions';
import { MatchAsset } from '../Search/SearchTable';

type ReportToolbarProperties = {
  onCollapseAll?: () => void;
  onExpandAll?: () => void;
  onToggleColumns: () => void;
  toolbarActions?: React.ReactNode;
};

type ReportNoRowsOverlayProperties = {
  isLoading?: boolean;
};

declare module '@mui/x-data-grid-pro' {
  // eslint-disable-next-line unicorn/prevent-abbreviations, @typescript-eslint/no-empty-object-type -- MUI's interface name, must use extends for module augmentation
  interface ToolbarPropsOverrides extends ReportToolbarProperties {}
  // eslint-disable-next-line unicorn/prevent-abbreviations, @typescript-eslint/no-empty-object-type -- MUI's interface name, must use extends for module augmentation
  interface NoRowsOverlayPropsOverrides extends ReportNoRowsOverlayProperties {}
}

export type ReportTreeRow = FolderContent & {
  gridRowId: string;
  rowType: 'asset' | 'match';
  matchedAssetId?: string;
  matchPercentage?: number;
  matches?: AssetMatchResponse[];
  sourceAsset?: FolderContent;
};

type FetchRowsResult = {
  assets: ReportGroupAsset[];
  pageData: { total: number };
};

function DeferredIconButton({
  onClick,
  title,
  children,
}: Readonly<{
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}>) {
  const theme = useTheme();
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!isPending) return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        onClick();
        setIsPending(false);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isPending, onClick]);

  return (
    <Tooltip title={title} followCursor enterDelay={0} enterNextDelay={0}>
      <Box
        sx={{
          position: 'relative',
          width: 28,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconButton onClick={() => setIsPending(true)} disabled={isPending} size='small'>
          {children}
        </IconButton>
        {isPending && (
          <CircularProgress
            size={26}
            thickness={2}
            sx={{
              position: 'absolute',
              top: 1,
              left: 1,
              color: theme.palette.primary.main,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

function ReportToolbar({
  onCollapseAll,
  onExpandAll,
  onToggleColumns,
  toolbarActions,
}: Readonly<ReportToolbarProperties>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        borderBottom: 1,
        borderColor: 'divider',
        padding: '4px 8px',
        gap: 0.5,
      }}
    >
      {onCollapseAll && (
        <DeferredIconButton onClick={onCollapseAll} title='Collapse all'>
          <UnfoldLess fontSize='small' />
        </DeferredIconButton>
      )}
      {onExpandAll && (
        <DeferredIconButton onClick={onExpandAll} title='Expand all'>
          <UnfoldMore fontSize='small' />
        </DeferredIconButton>
      )}
      <Tooltip title='Columns' followCursor enterDelay={0} enterNextDelay={0}>
        <IconButton onClick={onToggleColumns} size='small'>
          <ViewColumn fontSize='small' />
        </IconButton>
      </Tooltip>
      {toolbarActions}
    </Box>
  );
}

const ExpandIcon = () => <KeyboardArrowRight fontSize='small' />;
const CollapseIcon = () => <KeyboardArrowDown fontSize='small' />;

const getRowId = (row: ReportGroupAsset) => row.id;

const getDetailPanelHeight = (parameters: { row: ReportGroupAsset }) => {
  const matchCount = new Set(parameters.row.matches?.map(m => m.asset.id) ?? []).size;
  return matchCount * COMPACT_ROW_HEIGHT;
};

const ROW_HEIGHT = 80;
const COMPACT_ROW_HEIGHT = 56;

const OverlayContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

const OverlayMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const LoadingRowsOverlay = () => (
  <OverlayContainer>
    <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
    <OverlayMessage variant='body2'>Loading records…</OverlayMessage>
  </OverlayContainer>
);

const NoRowsOverlay = ({ isLoading }: ReportNoRowsOverlayProperties) => {
  if (isLoading) return <LoadingRowsOverlay />;

  return (
    <OverlayContainer>
      <OverlayMessage variant='body2'>No rows</OverlayMessage>
    </OverlayContainer>
  );
};

const MatchRowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: COMPACT_ROW_HEIGHT,
});

const MatchCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  padding: '0 10px',
  height: '100%',
});

function getCellValue(row: FolderContent, field: string): unknown {
  if (field in row) {
    return row[field as keyof FolderContent];
  }
  return undefined;
}

function buildCellParameters(matchRow: FolderContent, column: GridColDef) {
  // eslint-disable-next-line unicorn/no-null -- GridLeafNode requires `groupingKey: GridKeyValue | null`
  const rowNode = { type: 'leaf' as const, id: matchRow.id, depth: 0, parent: '', groupingKey: null };
  const parameters: GridRenderCellParams<FolderContent> = {
    row: matchRow,
    id: matchRow.id,
    field: column.field,
    value: getCellValue(matchRow, column.field),
    colDef: { ...column, computedWidth: 0 },
    cellMode: 'view',
    hasFocus: false,
    tabIndex: -1,
    api: {} as GridApiPro,
    rowNode,
    isEditable: false,
  };
  return parameters;
}

const MatchCellContent = ({
  field,
  match,
  sourceAsset,
  matchAssetRow,
  columnDefinition,
}: {
  field: string;
  match: AssetMatchResponse;
  sourceAsset: Asset;
  matchAssetRow: FolderContent;
  columnDefinition: GridColDef | undefined;
}) => {
  if (field === '__detail_panel_toggle__') {
    // eslint-disable-next-line unicorn/no-null -- React expects null for empty renders
    return null;
  }

  if (field === 'matchPercentage') {
    const matchAsset: MatchAsset = {
      ...match.asset,
      contentType: 'asset',
      matchPercentage: match.matchPercentage,
    };
    return <RenderPercentage value={match.matchPercentage} searchAsset={sourceAsset} matchAsset={matchAsset} />;
  }

  if (columnDefinition?.renderCell) {
    return columnDefinition.renderCell(buildCellParameters(matchAssetRow, columnDefinition));
  }

  // eslint-disable-next-line unicorn/no-null -- React expects null for empty renders
  return null;
};

const DetailPanel = ({
  row,
  api,
  columns,
  onRowContextMenu,
}: {
  row: ReportGroupAsset;
  api: GridApiPro;
  columns: GridColDef[];
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
}) => {
  const [visibleColumns, setVisibleColumns] = useState(() => api.getAllColumns().filter(col => col.computedWidth > 0));

  useEffect(() => {
    const update = () => setVisibleColumns(api.getAllColumns().filter(col => col.computedWidth > 0));
    const unsubscribeResize = api.subscribeEvent('columnWidthChange', update);
    const unsubscribeVisibility = api.subscribeEvent('columnVisibilityModelChange', update);
    return () => {
      unsubscribeResize();
      unsubscribeVisibility();
    };
  }, [api]);

  if (!row.matches || row.matches.length === 0) return;

  const sourceAsset: Asset = row;
  const seenIds = new Set<string>();

  const columnDefinitionsByField = new Map(columns.map(col => [col.field, col]));

  return (
    <Box>
      {row.matches
        .filter(match => {
          if (seenIds.has(match.asset.id)) return false;
          seenIds.add(match.asset.id);
          return true;
        })
        .map(match => {
          const matchAssetRow: FolderContent = {
            ...match.asset,
            contentType: 'asset' as const,
          };

          return (
            <MatchRowContainer key={match.asset.id} data-id={match.asset.id} onContextMenu={onRowContextMenu}>
              {visibleColumns.map(col => (
                <MatchCell
                  key={col.field}
                  data-field={col.field}
                  sx={{
                    width: col.computedWidth,
                    flexShrink: 0,
                    textAlign: col.type === 'number' ? 'right' : undefined,
                  }}
                >
                  <MatchCellContent
                    field={col.field}
                    match={match}
                    sourceAsset={sourceAsset}
                    matchAssetRow={matchAssetRow}
                    columnDefinition={columnDefinitionsByField.get(col.field)}
                  />
                </MatchCell>
              ))}
            </MatchRowContainer>
          );
        })}
    </Box>
  );
};

export const ReportGroupTable = ({
  fetchRows,
  error,
  isLoading,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  gridApiRef: externalGridApiReference,
  onRowContextMenu,
  enableRowDragging = false,
  toolbarActions,
  showMatchDetails = false,
  onMatchDataDetected,
}: {
  fetchRows?: (parameters: { page: number; perPage: number }) => Promise<FetchRowsResult>;
  error?: ApiErrorDisplay;
  isLoading: boolean;
  columns: GridColDef[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  gridApiRef?: React.RefObject<GridApiPro | null>;
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  enableRowDragging?: boolean;
  toolbarActions?: React.ReactNode;
  showMatchDetails?: boolean;
  onMatchDataDetected?: () => void;
}) => {
  const internalApiReference = useGridApiRef();
  const apiReference = externalGridApiReference ?? internalApiReference;

  const expandedIdsReference = useRef(new Set<string>());

  const expandNewRows = () => {
    const allRowIds = apiReference.current?.getAllRowIds() ?? [];
    const newIds = allRowIds.filter(id => !expandedIdsReference.current.has(String(id)));
    if (newIds.length > 0) {
      for (const id of newIds) {
        expandedIdsReference.current.add(String(id));
      }
      apiReference.current?.setExpandedDetailPanels(new Set(expandedIdsReference.current));
    }
  };

  useEffect(() => {
    if (!apiReference.current) return;
    const unsubscribe = apiReference.current.subscribeEvent('rowsSet', () => {
      if (onMatchDataDetected && !showMatchDetails) {
        const allRowIds = apiReference.current?.getAllRowIds() ?? [];
        const anyMatches = allRowIds.some(id => {
          const row = apiReference.current?.getRow<ReportGroupAsset>(id);
          return row?.matches && row.matches.length > 0;
        });
        if (anyMatches) onMatchDataDetected();
      }

      if (showMatchDetails) {
        expandNewRows();
      }
    });

    if (showMatchDetails) {
      expandNewRows();
    }

    return unsubscribe;
  }, [apiReference, showMatchDetails, onMatchDataDetected]);

  const getDetailPanelContent = showMatchDetails
    ? (parameters: GridRowParams<ReportGroupAsset>) => {
        const row = parameters.row;
        if (!row.matches || row.matches.length === 0 || !apiReference.current) return;
        return (
          <DetailPanel row={row} api={apiReference.current} columns={columns} onRowContextMenu={onRowContextMenu} />
        );
      }
    : undefined;

  const handleToggleColumns = () => {
    apiReference.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const handleExpandAll = () => {
    const allIds = apiReference.current?.getAllRowIds() ?? [];
    expandedIdsReference.current = new Set(allIds.map(String));
    apiReference.current?.setExpandedDetailPanels(new Set(allIds));
  };

  const handleCollapseAll = () => {
    expandedIdsReference.current.clear();
    apiReference.current?.setExpandedDetailPanels(new Set());
  };

  return (
    <DataGridLazy<ReportGroupAsset, { assets: ReportGroupAsset[] }>
      apiRef={apiReference}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      fetchRows={fetchRows}
      dataKey='assets'
      error={error}
      loading={isLoading}
      rowHeight={ROW_HEIGHT}
      columnHeaderHeight={40}
      density='compact'
      disableRowSelectionOnClick
      disableFilter
      getRowId={getRowId}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      showToolbar
      slots={{
        detailPanelExpandIcon: ExpandIcon,
        detailPanelCollapseIcon: CollapseIcon,
        toolbar: ReportToolbar,
        loadingOverlay: LoadingRowsOverlay,
        noRowsOverlay: NoRowsOverlay,
        ...(enableRowDragging && { row: DraggableRow }),
      }}
      slotProps={{
        ...(onRowContextMenu && {
          row: { onContextMenu: onRowContextMenu },
        }),
        noRowsOverlay: { isLoading },
        toolbar: {
          ...(showMatchDetails && { onCollapseAll: handleCollapseAll, onExpandAll: handleExpandAll }),
          onToggleColumns: handleToggleColumns,
          toolbarActions,
        },
      }}
      sx={{
        height: '100%',
        borderRadius: '6px',
        '& .MuiDataGrid-cell': {
          lineHeight: 'unset !important',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
    />
  );
};
